import React from 'react';
import { Facebook, Instagram, Mail, MessageCircle } from 'lucide-react';
import { businessInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const goTo = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(element, { offset: -80, duration: 1.4 });
    } else {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const socialLinks = {
    facebook: "https://facebook.com/colombianbodymedspa",
    instagram: "https://instagram.com/colombianbodymedspa",
    tiktok: "https://tiktok.com/@colombianbodymedspa",
    snapchat: "https://snapchat.com/add/colombianbodyms",
    threads: "https://threads.net/@colombianbodymedspa",
    whatsapp: "https://wa.me/14695366652",
    email: "mailto:colombianbodymedspa@icloud.com",
    emailInfo: "mailto:info@colombianbodycontour.us",
    squareBooking: "https://app.squareup.com/appointments/book/2z9qf4qks9dbay/LFHGNQ373D5BF/start"
  };

  return (
    <footer className="bg-black text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="space-y-4">
            <h3 className="text-xl font-normal">
              Colombian Body Contour Spa
            </h3>
            <p className="text-sm text-gray-400 leading-relaxed">
              {t.footer.tagline}
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium uppercase tracking-wider text-gray-400">
              Quick Links
            </h4>
            <nav className="flex flex-col space-y-2">
              <button
                onClick={() => goTo('about')}
                className="text-sm text-gray-300 hover:text-white transition-colors duration-200 text-left"
              >
                {t.nav.about}
              </button>
              <button
                onClick={() => goTo('services')}
                className="text-sm text-gray-300 hover:text-white transition-colors duration-200 text-left"
              >
                {t.nav.services}
              </button>
              <button
                onClick={() => goTo('gallery')}
                className="text-sm text-gray-300 hover:text-white transition-colors duration-200 text-left"
              >
                {t.nav.gallery}
              </button>
              <button
                onClick={() => goTo('contact')}
                className="text-sm text-gray-300 hover:text-white transition-colors duration-200 text-left"
              >
                {t.nav.contact}
              </button>
            </nav>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium uppercase tracking-wider text-gray-400">
              Contact
            </h4>
            <div className="space-y-2 text-sm text-gray-300">
              <p>{businessInfo.address}</p>
              <a
                href={`tel:${businessInfo.phone}`}
                className="block hover:text-white transition-colors duration-200"
              >
                {businessInfo.phone}
              </a>
              <p>{businessInfo.hours}</p>
            </div>
          </div>

          {/* Social Media */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium uppercase tracking-wider text-gray-400">
              Follow Us
            </h4>
            <div className="grid grid-cols-4 gap-3">
              <a
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-gray-600 flex items-center justify-center transition-colors duration-200 border border-gray-700"
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </a>
              <a
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-gray-600 flex items-center justify-center transition-colors duration-200 border border-gray-700"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href={socialLinks.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-gray-600 flex items-center justify-center transition-colors duration-200 border border-gray-700 text-xs font-bold"
                aria-label="TikTok"
              >
                TT
              </a>
              <a
                href={socialLinks.snapchat}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-gray-600 flex items-center justify-center transition-colors duration-200 border border-gray-700 text-xs font-bold"
                aria-label="Snapchat"
              >
                SC
              </a>
              <a
                href={socialLinks.threads}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-gray-600 flex items-center justify-center transition-colors duration-200 border border-gray-700 text-xs font-bold"
                aria-label="Threads"
              >
                TH
              </a>
              <a
                href={socialLinks.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-gray-600 flex items-center justify-center transition-colors duration-200 border border-gray-700"
                aria-label="WhatsApp"
              >
                <MessageCircle size={18} />
              </a>
              <a
                href={socialLinks.email}
                className="w-10 h-10 bg-gray-800 hover:bg-gray-600 flex items-center justify-center transition-colors duration-200 border border-gray-700"
                aria-label="Email - iCloud"
              >
                <Mail size={18} />
              </a>
              <a
                href={socialLinks.emailInfo}
                className="w-10 h-10 bg-gray-800 hover:bg-gray-600 flex items-center justify-center transition-colors duration-200 border border-gray-700"
                aria-label="Email - Info"
                title="info@colombianbodycontour.us"
              >
                <Mail size={18} />
              </a>
              <a
                href={socialLinks.squareBooking}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-book-appointment"
                className="w-10 h-10 bg-gray-800 hover:bg-gray-600 flex items-center justify-center transition-colors duration-200 border border-gray-700 text-xs font-bold"
                aria-label="Book Appointment"
              >
                SQ
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              © {currentYear} Colombian Body Contour Spa. {t.footer.rights}.
            </p>
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <button className="hover:text-white transition-colors duration-200">
                Privacy Policy
              </button>
              <button className="hover:text-white transition-colors duration-200">
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
